import { useState } from 'react';
import { Link } from 'react-router-dom';
import { testimonials } from '@/mocks/testimonials';

const milestones = [
  { year: '2018', title: '项目诞生', description: '创始团队发现市场上缺乏真正免费且好用的工具软件，决定开发91免费版，让每个人都能享受专业级工具服务。' },
  { year: '2019', title: '首个版本发布', description: '经过一年精心打磨，91免费版v1.0正式发布，首批提供系统优化和文件管理两大核心功能模块。' },
  { year: '2020', title: '突破百万用户', description: '凭借出色的用户体验和口碑传播，91免费版累计用户突破100万，成为国内最受欢迎的免费工具软件之一。' },
  { year: '2021', title: '功能大扩展', description: '新增办公软件、多媒体处理和安全防护三大功能模块，功能总数突破100项，满足更多使用场景。' },
  { year: '2022', title: '国际化起航', description: '推出英文版和多语言支持，开始服务全球用户。同年获得多家权威媒体推荐和行业奖项。' },
  { year: '2023', title: '500万用户里程碑', description: '累计用户突破500万，好评率高达98.7%。推出企业协作功能，开始服务中小型企业团队。' },
  { year: '2024', title: 'AI智能升级', description: '全面引入AI技术，推出智能文档处理、智能视频剪辑等功能，让工具使用更加智能高效。' },
  { year: '2025-2026', title: '持续进化', description: 'v4.0版本全面重构，性能大幅提升。持续优化用户体验，保持每月稳定更新节奏，始终走在行业前沿。' },
];

export default function About() {
  const [formState, setFormState] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('mobile_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormState('success');
      return;
    }

    try {
      const formData = new FormData(form);
      formData.delete('mobile_alt');
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, value.toString());
      });

      const response = await fetch('https://readdy.ai/api/form/d9pisupn2trhb35gvgd0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        setFormError('');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setFormState('error');
          setFormError('提交失败，请稍后重试');
        } else {
          setFormState('error');
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormState('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <main className="pt-24">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-4">
        <nav className="flex items-center gap-2 text-xs text-foreground-400 flex-wrap">
          <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-700 font-medium whitespace-nowrap">关于我们</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-foreground-900 mb-5">
            关于91免费版
          </h1>
          <p className="text-foreground-500 text-base leading-relaxed max-w-2xl mx-auto">
            91免费版诞生于2018年，由一群热爱技术、追求极致的开发者创建。我们的使命是让每个人都能免费使用专业级的工具软件，无需付费，无需妥协。八年来，我们始终坚持&ldquo;永久免费、安全可靠、持续进化&rdquo;的核心理念，为数百万用户提供优质服务。
          </p>
        </div>
      </section>

      {/* Brand Story Timeline */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-14">
            品牌发展历程
          </h2>

          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-px"></div>

            <div className="space-y-8">
              {milestones.map((m, i) => (
                <div
                  key={m.year}
                  className={`relative flex items-start gap-6 md:gap-10 ${
                    i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50 -translate-x-1/2 mt-1.5 z-10"></div>
                  <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'} pl-10 md:pl-0 md:px-10`}>
                    <span className="inline-block px-3 py-1 rounded-full bg-primary-50 text-primary-600 text-xs font-semibold mb-2">
                      {m.year}
                    </span>
                    <h3 className="font-heading font-semibold text-foreground-900 text-base mb-1.5">
                      {m.title}
                    </h3>
                    <p className="text-foreground-500 text-xs leading-relaxed">{m.description}</p>
                  </div>
                  <div className="flex-1 hidden md:block"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-14">
            我们的核心价值观
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-8 rounded-xl bg-background-50 border border-background-200/70">
              <div className="w-14 h-14 mx-auto mb-5 rounded-xl bg-primary-50 flex items-center justify-center">
                <i className="ri-heart-line text-primary-500 text-2xl"></i>
              </div>
              <h3 className="font-heading font-semibold text-foreground-900 mb-3 text-base">永久免费</h3>
              <p className="text-foreground-500 text-xs leading-relaxed">
                我们承诺核心功能永久免费，不设任何付费墙或功能限制。让每个人都能平等地使用专业工具，是我们的初心。
              </p>
            </div>
            <div className="text-center p-8 rounded-xl bg-background-50 border border-background-200/70">
              <div className="w-14 h-14 mx-auto mb-5 rounded-xl bg-accent-50 flex items-center justify-center">
                <i className="ri-shield-star-line text-accent-500 text-2xl"></i>
              </div>
              <h3 className="font-heading font-semibold text-foreground-900 mb-3 text-base">安全可靠</h3>
              <p className="text-foreground-500 text-xs leading-relaxed">
                用户隐私和数据安全是我们的底线。所有功能经过严格安全审查，不收集非必要数据，不上传用户文件。
              </p>
            </div>
            <div className="text-center p-8 rounded-xl bg-background-50 border border-background-200/70">
              <div className="w-14 h-14 mx-auto mb-5 rounded-xl bg-secondary-50 flex items-center justify-center">
                <i className="ri-rocket-line text-secondary-500 text-2xl"></i>
              </div>
              <h3 className="font-heading font-semibold text-foreground-900 mb-3 text-base">持续进化</h3>
              <p className="text-foreground-500 text-xs leading-relaxed">
                技术日新月异，我们从不止步。每月稳定更新，持续优化性能、添加新功能、适配新系统，始终提供最好的体验。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* User Testimonials */}
      <section className="py-16 md:py-20 bg-background-100" id="testimonials">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-14">
            用户真实口碑
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {testimonials.map((t) => (
              <div key={t.id} className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i
                      key={i}
                      className={`text-xs ${
                        i < t.rating ? 'ri-star-fill text-primary-400' : 'ri-star-line text-secondary-300'
                      }`}
                    ></i>
                  ))}
                </div>
                <p className="text-foreground-600 text-sm leading-relaxed mb-4">
                  &ldquo;{t.content}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <img src={t.avatar} alt={t.name} className="w-9 h-9 rounded-full object-cover" />
                  <div>
                    <div className="font-heading font-semibold text-foreground-900 text-sm">{t.name}</div>
                    <div className="text-foreground-400 text-xs">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 md:py-20 bg-background-50" id="contact">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-4">
            联系我们
          </h2>
          <p className="text-foreground-500 text-sm text-center mb-10">
            有任何问题或建议？请随时与我们联系，我们会在24小时内回复
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
            <div className="p-6 rounded-xl bg-background-100 text-center">
              <div className="w-11 h-11 mx-auto mb-3 rounded-lg bg-primary-50 flex items-center justify-center">
                <i className="ri-mail-line text-primary-500 text-xl"></i>
              </div>
              <h4 className="font-heading font-semibold text-foreground-900 text-sm mb-1">邮箱</h4>
              <p className="text-foreground-500 text-xs">support@91free.com</p>
            </div>
            <div className="p-6 rounded-xl bg-background-100 text-center">
              <div className="w-11 h-11 mx-auto mb-3 rounded-lg bg-accent-50 flex items-center justify-center">
                <i className="ri-wechat-line text-accent-500 text-xl"></i>
              </div>
              <h4 className="font-heading font-semibold text-foreground-900 text-sm mb-1">微信公众号</h4>
              <p className="text-foreground-500 text-xs">91免费版官方</p>
            </div>
            <div className="p-6 rounded-xl bg-background-100 text-center">
              <div className="w-11 h-11 mx-auto mb-3 rounded-lg bg-secondary-50 flex items-center justify-center">
                <i className="ri-group-line text-secondary-500 text-xl"></i>
              </div>
              <h4 className="font-heading font-semibold text-foreground-900 text-sm mb-1">用户社群</h4>
              <p className="text-foreground-500 text-xs">QQ群：88888888</p>
            </div>
          </div>

          {formState === 'success' ? (
            <div className="text-center p-10 rounded-xl bg-accent-50 border border-accent-200">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                <i className="ri-check-line text-accent-600 text-2xl"></i>
              </div>
              <h3 className="font-heading font-semibold text-foreground-900 text-lg mb-2">提交成功！</h3>
              <p className="text-foreground-500 text-sm mb-4">感谢您的留言，我们会在24小时内回复</p>
              <button
                onClick={() => setFormState('idle')}
                className="px-5 py-2 bg-accent-500 text-background-50 rounded-lg text-sm font-medium hover:bg-accent-600 transition-all cursor-pointer whitespace-nowrap"
              >
                发送新消息
              </button>
            </div>
          ) : (
            <form onSubmit={handleContactSubmit} className="space-y-5" data-readdy-form="">
              {formState === 'error' && (
                <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                  {formError || '提交失败，请稍后重试'}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                  主题 <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="请输入消息主题"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-red-400">*</span>
                </label>
                <textarea
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                ></textarea>
                <p className="text-foreground-400 text-xs mt-1 text-right">最多500字</p>
              </div>

              {/* Honeypot */}
              <input
                type="text"
                name="mobile_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="absolute opacity-0 pointer-events-none"
              />

              <button
                type="submit"
                className="w-full py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
              >
                提交留言
              </button>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}